import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router';

import * as graphql from '@treams/treams-network';

import type {
	GroupSelection,
	SelectableGroupFilter,
} from '../../../goals/group-selector/new/GroupSelector.types';
import type { TreamsScoreLocationState } from '../AnalyticsTreamsScoreOverviewCard';
import type { TreamsScoreRow } from '../TreamsScore.types';

const ALL_DEPARTMENTS_ID = '__all_departments__';
const ALL_TEAMS_ID = '__all_teams__';

/*
 * Adapts the flat Treams score rows to the GroupSelectorNew component so it
 * can drive the table filter. The rows already contain every group, so
 * selection state is managed locally instead of through useGroupSelection.
 *
 * Two synthetic "select all" entries are added, one per group type. Picking
 * one resolves to every row of that type; every other pick resolves to
 * itself. The synthetic entries carry an empty inline member list so the
 * Preview popover doesn't query their ids; row groups omit inline members,
 * letting Preview fetch them by group id on demand.
 */
export function useScoreGroupFilter(rows: TreamsScoreRow[]) {
	const { t } = useTranslation();
	const location = useLocation<TreamsScoreLocationState | undefined>();
	const [groupSelection, setGroupSelection] = useState<GroupSelection[]>([]);
	const seededFromLocationRef = useRef(false);

	const allGroups = useMemo<graphql.Group[]>(() => {
		// "Alle afdelingen" doubles as the structural root. Every other entry hangs
		// under it so the modal's search (which navigates to a match's parent) can
		// resolve leaves instead of falling back to "not found". Selection stays
		// type-based, so the parent links only drive rendering/search.
		const rowGroups = rows.map((row) => ({
			id: row.id,
			name: row.name,
			type: row.type as graphql.GroupType,
			parent: { id: ALL_DEPARTMENTS_ID },
			isArchived: false,
		}));

		const allDepartments = {
			id: ALL_DEPARTMENTS_ID,
			name: t('analytics.treamsScore.filter.allDepartments'),
			type: graphql.GroupType.Department,
			parent: null,
			isArchived: false,
			members: { edges: [] },
		};

		const allTeams = {
			id: ALL_TEAMS_ID,
			name: t('analytics.treamsScore.filter.allTeams'),
			type: graphql.GroupType.Team,
			parent: { id: ALL_DEPARTMENTS_ID },
			isArchived: false,
			members: { edges: [] },
		};

		return [allDepartments, allTeams, ...rowGroups] as unknown as graphql.Group[];
	}, [rows, t]);

	useEffect(() => {
		if (seededFromLocationRef.current || rows.length === 0) return;

		const initialIds = location.state?.selectedGroupIds;
		seededFromLocationRef.current = true;
		if (!initialIds || initialIds.length === 0) return;

		const idSet = new Set(initialIds);
		const matched = allGroups
			.filter(
				(group) =>
					idSet.has(group.id) && group.id !== ALL_DEPARTMENTS_ID && group.id !== ALL_TEAMS_ID,
			)
			.map((group) => ({ group, recursive: false }));

		if (matched.length > 0) {
			setGroupSelection(matched);
		}
	}, [rows.length, allGroups, location.state]);

	const canSelect = useCallback<SelectableGroupFilter>(() => true, []);

	const hasExplicitGroupSelection = groupSelection.length > 0;

	// Resolve "Alle afdelingen"/"Alle teams" to every row of their type; any other
	// pick resolves to itself, so the table filter sees the actual rows.
	const groupsInSelection = useMemo<graphql.Group[]>(() => {
		const rowsOfType = (type: graphql.GroupType) =>
			allGroups.filter(
				(group) =>
					group.type === type && group.id !== ALL_DEPARTMENTS_ID && group.id !== ALL_TEAMS_ID,
			);

		const seen = new Set<string>();
		const result: graphql.Group[] = [];

		const add = (group: graphql.Group) => {
			if (seen.has(group.id)) return;
			seen.add(group.id);
			result.push(group);
		};

		for (const { group } of groupSelection) {
			add(group);

			if (group.id === ALL_DEPARTMENTS_ID) rowsOfType(graphql.GroupType.Department).forEach(add);
			if (group.id === ALL_TEAMS_ID) rowsOfType(graphql.GroupType.Team).forEach(add);
		}

		return result;
	}, [allGroups, groupSelection]);

	const filteredRows = useMemo(() => {
		if (!hasExplicitGroupSelection) return rows;

		const selectedIds = new Set(groupsInSelection.map((group) => group.id));

		return rows.filter((row) => selectedIds.has(row.id));
	}, [rows, hasExplicitGroupSelection, groupsInSelection]);

	return {
		allGroups,
		canSelect,
		groupSelection,
		groupsInSelection,
		hasExplicitGroupSelection,
		updateExplicitGroupSelection: setGroupSelection,
		filteredRows,
	};
}
